import React, { useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';
import { Helmet } from 'react-helmet';

import { Alert, Box, Button, CircularProgress, Container, Divider, Stack, Typography } from '@mui/material';

import PageHeader from '~/components/Common/Elements/PageHeader';
import { preRegisterSpotlightRoutes } from '~/components/Common/Spotlight/SpotlightProvider';

import { LUX_PLUS } from '~/consts/membership';

import { useGetLuxPlusScheduleDefaults } from '~/hooks/apis/offers/useGetLuxPlusScheduleDefaults';
import { useUpdateLuxPlusScheduleDefaults } from '~/hooks/apis/offers/useUpdateLuxPlusScheduleDefaults';

import DefaultsMatrix from './DefaultsMatrix';
import {
  buildCellKey,
  buildUpdates,
  describeApiError,
  dirtyKeysFrom,
  keysForAll,
  keysForRegion,
  keysForTier,
  provenanceFromRows,
  savedFromRows,
} from './luxPlusScheduleDefaults';

const BRAND = LUX_PLUS.BRANDS[0];
const REGIONS = LUX_PLUS.REGIONS;
const COPY_MAX_WIDTH = 900;

// Module-level side effect, so this page must stay statically imported in App.tsx or the entry
// registers after SpotlightProvider renders.
preRegisterSpotlightRoutes([
  {
    path: '/offers/lux-plus-schedule-defaults',
    label: 'Offers / LuxPlus+ Schedule Defaults',
    section: 'Offers',
    keywords: 'luxplus lp member schedule defaults region tier flash automation',
  },
]);

export default function LuxPlusScheduleDefaultsPage() {
  const { enqueueSnackbar } = useSnackbar();
  const { data, isLoading, error, refetch } = useGetLuxPlusScheduleDefaults();
  const { mutateAsync: saveDefaults, isPending: isSaving } = useUpdateLuxPlusScheduleDefaults();

  // Touched cells only, so a background refetch cannot wipe unsaved edits.
  const [changes, setChanges] = useState<Record<string, boolean>>({});

  const tiers = data?.availableTiers ?? [];

  const saved = useMemo(() => savedFromRows(data?.scheduleDefaults), [data]);

  const provenance = useMemo(() => provenanceFromRows(data?.scheduleDefaults), [data]);

  const enabled = useMemo(() => ({ ...saved, ...changes }), [saved, changes]);

  const dirtyKeys = useMemo(() => dirtyKeysFrom(changes, saved), [changes, saved]);

  const setCells = (keys: Array<string>, nextEnabled: boolean) => {
    setChanges((current) => {
      const next = { ...current };
      keys.forEach((key) => {
        next[key] = nextEnabled;
      });
      return next;
    });
  };

  const handleToggleCell = (region: string, tier: string) => {
    const key = buildCellKey(region, tier);
    setCells([key], !enabled[key]);
  };

  const handleToggleRegion = (region: string, nextEnabled: boolean) => {
    setCells(keysForRegion(region, tiers), nextEnabled);
  };

  const handleToggleTier = (tier: string, nextEnabled: boolean) => {
    setCells(keysForTier(tier, REGIONS), nextEnabled);
  };

  const handleToggleAll = (nextEnabled: boolean) => {
    setCells(keysForAll(REGIONS, tiers), nextEnabled);
  };

  const handleSave = async () => {
    try {
      await saveDefaults(buildUpdates(dirtyKeys, changes, BRAND));

      const refreshed = await refetch();
      if (refreshed.isError) {
        enqueueSnackbar('Saved, but the matrix could not be refreshed, so your changes still show as unsaved.', {
          variant: 'warning',
        });
        return;
      }

      setChanges({});
      enqueueSnackbar('LuxPlus+ schedule defaults saved', { variant: 'success' });
    } catch (saveError) {
      enqueueSnackbar(`Could not save LuxPlus+ schedule defaults: ${describeApiError(saveError)}`, {
        variant: 'error',
      });
    }
  };

  return (
    <Container maxWidth="xl">
      <Helmet>
        <title>LuxPlus+ Schedule Defaults</title>
      </Helmet>

      <PageHeader title="LuxPlus+ Schedule Defaults" backButton="/offers" />

      <Typography variant="body2" color="text.secondary" maxWidth={COPY_MAX_WIDTH}>
        New flash offers in the regions and tiers ticked below will automatically get a LuxPlus+ schedule matching their
        online purchaseable window. Existing schedules are never changed.
      </Typography>

      {isLoading && (
        <Box mt={4} display="flex" justifyContent="center">
          <CircularProgress />
        </Box>
      )}

      {!!error && (
        <Box mt={4}>
          <Alert severity={data ? 'warning' : 'error'}>
            Could not load LuxPlus+ schedule defaults: {describeApiError(error)}
            {data ? ' Showing the values from the last successful load.' : ''}
          </Alert>
        </Box>
      )}

      {!!data && tiers.length === 0 && (
        <Box mt={4}>
          <Alert severity="error">
            No offer tiers were returned, so there is nothing to configure. This is a server-side problem, not a
            configuration you can fix here.
          </Alert>
        </Box>
      )}

      {!!data && tiers.length > 0 && (
        <Box mt={3}>
          <DefaultsMatrix
            regions={REGIONS}
            tiers={tiers}
            enabled={enabled}
            provenance={provenance}
            onToggleCell={handleToggleCell}
            onToggleRegion={handleToggleRegion}
            onToggleTier={handleToggleTier}
            onToggleAll={handleToggleAll}
          />

          <Box position="sticky" bottom={0} zIndex={2}>
            <Divider />
            <Stack
              direction="row"
              spacing={2}
              paddingY={2}
              alignItems="center"
              justifyContent="flex-end"
              sx={{ backgroundColor: 'background.paper' }}
            >
              <Typography variant="body2" color="text.secondary" flex={1}>
                {dirtyKeys.length === 0
                  ? 'No changes'
                  : `${dirtyKeys.length} unsaved ${dirtyKeys.length === 1 ? 'change' : 'changes'}`}
              </Typography>
              <Button variant="text" disabled={dirtyKeys.length === 0 || isSaving} onClick={() => setChanges({})}>
                Discard
              </Button>
              <Button
                variant="contained"
                color="primary"
                disabled={dirtyKeys.length === 0 || isSaving}
                onClick={handleSave}
              >
                Save changes
              </Button>
            </Stack>
          </Box>
        </Box>
      )}
    </Container>
  );
}
